import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Loader } from 'rsuite';
import {
  getCampaignLandingData,
  getCampaignElements,
  getReadyToSubmit,
  getIsSubmittingForProcessing,
  submitCampaignSubmission,
  getIsCampaignPreview,
  previewSubmitCampaignSubmission,
  getCampaignBanner,
  getUseCampaignBanner,
  getCampaignBrandFontColor,
  getCampaignLanguage,
  getDisableSubmitButton,
} from '../../redux/modules/campaignLandingPage';
import { SUBMISSION_TYPE_SELECTOR_ELEMENT_TYPE_ID } from '../../constants/element-types';
import crakIds from '../../constants/crak';
import styles from './SubmitResponseButton.module.less';
import { useVpSnackbar } from '../../vp-ui';
import { StyledBrandingButton, StyledOutlineBrandingButton } from './styles';
import { getTranslation } from '../../translations/dictionary';

// Helper to format submission type for display
const formatSubmissionType = (types) => {
  if (!types || types.length === 0) return '';
  const typeLabels = {
    video: 'Video',
    image: 'Image',
    text: 'Text'
  };
  return types.map(t => typeLabels[t] || t).join(', ');
};

const buildSubmissionTypeSelectorPayload = (el) => {
  const selectedTypes = el?.selectedSubmissionTypes || [];
  const collectorData = el?.value || {};

  // Get the text content if text is selected
  const textContent = selectedTypes.includes('text')
    ? (collectorData.text?.textContent || '')
    : '';

  // Build a simple, readable value:
  // - First part: submission type(s) chosen
  // - Second part (if text): the actual text content
  const submissionTypeLabel = formatSubmissionType(selectedTypes);

  // Value format: "Video, Text" or just the type if no text content
  // Text content will be stored separately for display
  const valuePayload = {
    type: submissionTypeLabel,
    textResponse: textContent || null
  };

  return {
    campaignElementId: el.id,
    value: JSON.stringify(valuePayload),
    mediaSubmissionId: el?.mediaSubmissionId,
    selected: selectedTypes.length > 0,
    attachment: collectorData.image?.attachment
  };
};

// Helper to check if element is a Submission Type Selector
const isSubmissionTypeSelectorElement = (el) => {
  return (
    el.elementTypeId === SUBMISSION_TYPE_SELECTOR_ELEMENT_TYPE_ID ||
    el.crakId === crakIds.SUBMISSION_TYPE_SELECTOR
  );
};

const buildPayload = (campaignLandingData, campaignElements) => {
  const elementsAsArray = Object.values(campaignElements);

  return {
    campaignId: campaignLandingData.campaignId,
    responses: elementsAsArray.map((el) => {
      // Special handling for Submission Type Selector element
      if (isSubmissionTypeSelectorElement(el)) {
        return buildSubmissionTypeSelectorPayload(el);
      }

      // Handle standard elements - extract text value if present
      const textValue = el?.value?.text?.textContent;
      const value = textValue || el.value;

      return {
        campaignElementId: el.id,
        value: typeof value === 'object' ? JSON.stringify(value) : value,
        mediaSubmissionId: el?.mediaSubmissionId,
        selected: el.selected,
        attachment: el?.attachment || el?.value?.image?.attachment
      };
    })
  };
};

const SubmitResponseButton = ({
  _submitCampaignSubmission,
  readyToSubmit,
  campaignLandingData,
  campaignElements,
  isSubmittingForProcessing,
  isPreview,
  _previewSubmitCampaignSubmission,
  backgroundColor = '',
  color = '',
  useBanner = false,
  language,
  disableSubmitButton
}) => {
  const submitLabel = getTranslation('submit', language) || 'Submit';

  const { addSnackbar } = useVpSnackbar();

  const handleSubmit = () => {
    if (isPreview) {
      _previewSubmitCampaignSubmission();

      addSnackbar({
        severity: 'warning',
        title: 'No Submission',
        message: `No submission has been sent because this is a preview of your Campaign. You can find the correct link under Share -> Copy Campaign Link, on your Campaign's editing page.`
      });
    } else {
      const payload = buildPayload(campaignLandingData, campaignElements);
      _submitCampaignSubmission(payload);
    }
  };

  const useCustomeStyle = useBanner && backgroundColor && color;
  const isWhitePrimaryColor = backgroundColor === 'rgb(255 255 255 / 1)';

  const buttonProps = {
    component: 'label',
    variant: isWhitePrimaryColor ? 'outlined' : 'contained',
    disabled: disableSubmitButton || isPreview || !readyToSubmit,
    id: 'vpSubmitResponseButton',
    backgroundColor: backgroundColor,
    fontColor: color,
    custom: useCustomeStyle,
    onClick: handleSubmit,
    sx: { width: '200px', mt: '25px' }
  };

  return isWhitePrimaryColor ? (
    <StyledOutlineBrandingButton data-testid="submitResponse" {...buttonProps}>
      {isSubmittingForProcessing ? <Loader className={styles.vpLoadingSpinner} /> : submitLabel}
    </StyledOutlineBrandingButton>
  ) : (
    <StyledBrandingButton data-testid="submitResponse" {...buttonProps}>
      {isSubmittingForProcessing ? <Loader className={styles.vpLoadingSpinner} /> : submitLabel}
    </StyledBrandingButton>
  );
};

SubmitResponseButton.propTypes = {
  _submitCampaignSubmission: PropTypes.func,
  readyToSubmit: PropTypes.bool,
  campaignLandingData: PropTypes.object,
  campaignElements: PropTypes.object,
  isSubmittingForProcessing: PropTypes.bool,
  isPreview: PropTypes.bool,
  _previewSubmitCampaignSubmission: PropTypes.func,
  backgroundColor: PropTypes.string,
  color: PropTypes.string,
  useBanner: PropTypes.bool,
  language: PropTypes.string
};

const mapStateToProps = (state) => ({
  readyToSubmit: getReadyToSubmit(state),
  campaignLandingData: getCampaignLandingData(state),
  campaignElements: getCampaignElements(state),
  isSubmittingForProcessing: getIsSubmittingForProcessing(state),
  isPreview: getIsCampaignPreview(state),
  backgroundColor: getCampaignBanner(state),
  useBanner: getUseCampaignBanner(state),
  color: getCampaignBrandFontColor(state),
  language: getCampaignLanguage(state),
  disableSubmitButton: getDisableSubmitButton(state)
});

const mapDispatchToProps = {
  _submitCampaignSubmission: submitCampaignSubmission,
  _previewSubmitCampaignSubmission: previewSubmitCampaignSubmission
};

export default connect(mapStateToProps, mapDispatchToProps)(SubmitResponseButton);



// WEBPACK FOOTER //
// ./src/components/CampaignLanding/SubmitResponseButton.jsx